<template>
	<div id="account">
		<!-- delete dialog -->
		<v-delete-dialog
			v-model="deleteDialog"
			module-type="accounts"
			:ids="$route.params.id"
			@on-delete="$emit('update-accounts')"
		>
			{{$T('are-you-sure-you-want-to-$', { message: $T('the-item') })}}
		</v-delete-dialog>
		
		<!-- archive dialog -->
		<v-dialog
			v-model="archiveDialog"
			:persistent="changeArchiveStatusLoading"
			max-width="440"
		>
			<v-card>
				<v-card-title class="justify-center text-capitalize blue--text text--darken-2">
					<v-btn :disabled="changeArchiveStatusLoading" @click="archiveDialog = false" icon>
						<v-icon>$close</v-icon>
					</v-btn>
					<v-spacer/>
					<span class="me-8">
						{{$T('change-archive-status')}}
					</span>
					<v-spacer/>
				</v-card-title>
				<v-card-text class="pb-0">
					<p class="text-subtitle-1 mb-0">
						{{$T('change-archive-status-alert')}}
					</p>
				</v-card-text>
				<v-card-actions>
					<v-spacer/>
					<v-btn
						type="submit"
						color="blue darken-2 white--text"
						:loading="changeArchiveStatusLoading"
						@click="changeArchiveStatus()"
					>
						{{!isArchived ? $T('archive') : $T('unarchive')}}
					</v-btn>
					<v-btn
						@click="archiveDialog = false"
						:disabled="changeArchiveStatusLoading"
					>
						{{$T('close')}}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- buttons -->
		<div v-if="permissions.accounts.isReadWrite" class="d-flex mb-2">
			<v-spacer/>
			<template v-if="$route.query.preview">
				<v-btn
					height="32"
					class="me-2"
					color="blue darken-2 white--text"
					key="archive"
					replace
					:outlined="!isArchived"
					@click="archiveDialog = true"
				>
					<v-icon class="me-1">mdi-{{!isArchived ? 'archive-outline' : 'archive'}}</v-icon>
					{{!isArchived ? $T('archive') : $T('unarchive')}}
				</v-btn>

				<v-btn
					height="32"
					class="me-2"
					color="warning-color white--text"
					key="edit"
					replace
					:to="{ name: 'account', params: { id: $route.params.id } }"
				>
					{{$T('edit')}}
				</v-btn>
				<v-btn
					height="32"
					color="alert-color white--text"
					key="delete"
					@click="deleteDialog = true"
				>
					{{$T('delete')}}
				</v-btn>
			</template>
			<template v-else>
				<v-btn
					height="32"
					class="me-2"
					color="primary"
					key="save"
					:loading="submitLoading"
					@click="submit"
				>
					{{$T('save')}}
				</v-btn>
				<v-btn
					height="32"
					key="cancel"
					:disabled="submitLoading"
					replace
					:to="{
						name: $route.params.id ? 'account' : 'accounts',
						params: { id: $route.params.id },
						query: { preview: 1 }
					}"
				>
					{{$T('close')}}
				</v-btn>
			</template>
		</div>

		<v-form ref="form" :disabled="Boolean(disableFieldsToPreview) || submitLoading">
			<v-card>
				<v-card-text class="pa-2">
					<p class="text-h6 grey--text text--darken-3 mb-0">
						{{$T('main-info')}}
					</p>
					<v-row dense align="center" class="ma-0">
						<!-- account name -->
						<v-col lg="3" md="4" sm="6" cols="12" class="py-1">
							<v-text-field
								v-model="name"
								:label="$T('inputs.account-name')"
								outlined
								dense
								hide-details
								:loading="loading"
								:rules="rules.required"
							></v-text-field>
						</v-col>

						<!-- parent account -->
						<v-col lg="3" md="4" sm="6" cols="12" class="py-1">
							<v-autocomplete
								v-model="parentAccountId"
								:items="accounts.filter(c => c.accountType === type.main)"
								item-value="id"
								item-text="name"
								:label="$T('inputs.parent-account')"
								outlined
								dense
								clearable
								hide-details
								:loading="loading"
								@change="suggestAccountNo()"
							></v-autocomplete>
						</v-col>

						<!-- account type -->
						<v-col lg="3" md="4" sm="6" cols="12" class="py-1">
							<v-autocomplete
								v-model="accountType"
								:items="accountTypes"
								item-value="id"
								item-text="name"
								:label="$T('inputs.type')"
								outlined
								dense
								hide-details
								:loading="loading"
								:rules="rules.requiredAsNumber"
							></v-autocomplete>
						</v-col>
						
						<!-- account no -->
						<v-col lg="3" md="4" sm="6" cols="12" class="py-1">
							<v-text-field
								v-model.number="accountNo"
								type="number"
								min="1"
								:label="$T('inputs.automatic-or-manual-no')"
								hide-spin-buttons
								outlined
								dense
								:prefix="noPrefix"
								:persistent-placeholder="Boolean(noPrefix)"
								hide-details
								:loading="loading || suggestLoading"
								:rules="rules.requiredAsNumber"
							></v-text-field>
						</v-col>

						<!-- country -->
						<v-col lg="3" md="4" sm="6" cols="12" class="py-1">
							<v-autocomplete
								v-model="country"
								:items="countries"
								item-value="name"
								item-text="name"
								:label="$T('inputs.country')"
								outlined
								dense
								hide-details
								:loading="loading"
							></v-autocomplete>
						</v-col>
						
						<!-- notes -->
						<v-col lg="9" md="8" sm="6" cols="12" class="py-1">
							<v-textarea
								v-model="notes"
								:label="$T('inputs.notes')"
								rows="1"
								auto-grow
								no-resize
								outlined
								dense
								hide-details
								:loading="loading"
							></v-textarea>
						</v-col>
					</v-row>
				</v-card-text>
			</v-card>
		</v-form>
	</div>
</template>

<script>
import rules from '@/helpers/validation-rules'
import countries from '@/json/countries-codes.json'
import { mapGetters, mapState } from 'vuex'
import { type, types } from '@/helpers/enums'
export default {
	name: 'Account',

	data() {
		return {
			loading: false,
			submitLoading: false,
			suggestLoading: false,

			deleteDialog: false,

			archiveDialog: false,
			changeArchiveStatusLoading: false,

			noPrefix: null,
			countries,
			type,
			rules,

			// data
			name: null,
			accountNo: null,
			parentAccountId: null,
			accountType: null,
			country: null,
			notes: null,
			isArchived: null,

			accountTypes: types,
		}
	},

	watch: {
		$route: {
			handler(val) {
				this.noPrefix = null;
				if (val.params.id) {
					const item = this.getAccountById(val.params.id);
					this.name = item.name;
					this.accountNo = item.innerAccountingNo;
					this.accountType = item.accountType;
					this.country = item.country;
					this.parentAccountId = item.parentId;
					this.notes = item.notes;
					this.isArchived = item.isArchived;

					const parent = this.getAccountById(this.parentAccountId);
					this.noPrefix = parent ? parent.accountingNo : null;

					if (val.query.delete) this.deleteDialog = true;
				} else if (this.$route.name === 'account') {
					this.$refs.form?.reset();
					if (
						val.query.subOf &&
						this.getAccountById(val.query.subOf).accountType === type.main
					) {
						this.parentAccountId = this.$route.query.subOf;
						this.accountType = type.sub;
						this.suggestAccountNo();
					};
				}
			},
			immediate: true
		}
	},

	computed: {
		...mapState({
			permissions: state => state.auth.self.permissions,
			accounts: state => state.accounts.accounts.filter(c => !c.isArchived)
		}),
		...mapGetters({
			getAccountById: 'accounts/getAccountById'
		}),
		disableFieldsToPreview() {
			return this.$route.query.preview || this.permissions.accounts.isReadonly;
		}
	},

	methods: {
		suggestAccountNo() {
			const parent = this.getAccountById(this.parentAccountId);
			this.noPrefix = parent ? parent.accountingNo : null;
			
			if (this.parentAccountId) {
				this.suggestLoading = true;
				this.$store.dispatch('accounts/fetchSuggestedNo', {
					parentId: this.parentAccountId
				}).then(data => {
					this.accountNo = data;
				}).finally(() => {
					this.suggestLoading = false;
				})
			}
		},

		changeArchiveStatus() {
			this.changeArchiveStatusLoading = true;
			this.$store.dispatch('accounts/updateArchiveState', {
				id: this.$route.params.id,
				isArchived: !this.isArchived
			}).then(() => {
				this.$emit('update-accounts', 'archive');
			}).finally(() => {
				this.changeArchiveStatusLoading = false;
			})
		},

		submit() {
			if (!this.$refs.form.validate()) return;
			
			this.submitLoading = true;
			const actionName = !this.$route.params.id ? 'create' : 'update'
			this.$store.dispatch(`accounts/${actionName}`, {
				id: this.$route.params.id,
				name: this.name,
				innerAccountingNo: this.accountNo,
				parentId: this.parentAccountId,
				accountType: this.accountType,
				notes: this.notes || '',
				country: this.country || ''
			}).then((id) => {
				this.$emit('update-accounts', id || this.$route.params.id);
			}).finally(() => {
				this.submitLoading = false;
			})
		}
	}
}
</script>

<style lang="scss">
#account {
	.v-text-field__prefix {
		padding: 0;
		font-weight: 500;
		color: #015931;
	}
	.v-input--is-disabled .v-text-field__prefix {
		color: rgba(0, 0, 0, 0.38);
	}
}
</style>